import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TAB_NAVIGATION_IDS, TAB_NAVIGATION_ITEMS_SORTED } from './constants';

type TabsNavigationContext = {
    selectedTabId: TAB_NAVIGATION_IDS;
    setIsTabIntersecting: (
        tabId: TAB_NAVIGATION_IDS,
        isIntersecting: boolean,
    ) => void;
};

const DEFAULT_TAB = TAB_NAVIGATION_IDS.INFO;
const DEFAULT_TAB_NAVIGATION_INTERSECTIONS = {
    [TAB_NAVIGATION_IDS.INFO]: false,
    [TAB_NAVIGATION_IDS.DETAILS]: false,
    [TAB_NAVIGATION_IDS.ORGANIZER]: false,
    [TAB_NAVIGATION_IDS.RELATED]: false,
    [TAB_NAVIGATION_IDS.FAQ]: false,
};

export const TabsNavigationContext = React.createContext<TabsNavigationContext>(
    {
        selectedTabId: DEFAULT_TAB,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setIsTabIntersecting: () => {},
    },
);

export const TabsNavigationProvider: React.FC = ({ children }) => {
    const [selectedTabId, setSelectedTabId] = useState(DEFAULT_TAB);
    const [tabNavigationIntersections, setTabNavigationIntersections] =
        useState(DEFAULT_TAB_NAVIGATION_INTERSECTIONS);

    const setIsTabIntersecting = useCallback(
        (tabId: TAB_NAVIGATION_IDS, isIntersecting: boolean) => {
            setTabNavigationIntersections((tabs) => ({
                ...tabs,
                [tabId]: isIntersecting,
            }));
        },
        [setTabNavigationIntersections],
    );

    const contextValue = useMemo(() => {
        return { selectedTabId, setIsTabIntersecting };
    }, [selectedTabId, setIsTabIntersecting]);

    useEffect(() => {
        const tabIdToSelect =
            TAB_NAVIGATION_ITEMS_SORTED.find(
                (tabId) => tabNavigationIntersections[tabId],
            ) ?? DEFAULT_TAB;

        setSelectedTabId(tabIdToSelect);
    }, [tabNavigationIntersections]);

    return (
        <TabsNavigationContext.Provider value={contextValue}>
            {children}
        </TabsNavigationContext.Provider>
    );
};
